import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { whyChooseUs } from '../mockData';
import * as LucideIcons from 'lucide-react';

const WhyChooseUs = () => {
  const getIcon = (iconName) => {
    const Icon = LucideIcons[iconName];
    return Icon ? <Icon className="w-10 h-10" /> : <LucideIcons.Droplet className="w-10 h-10" />;
  };

  const comparisonData = [
    { feature: "Upfront Cost", us: "₹0", others: "₹15,000 - ₹25,000" },
    { feature: "Installation", us: "Free", others: "₹1,500 - ₹3,000" },
    { feature: "Maintenance", us: "Included in rental", others: "Extra charges" },
    { feature: "Filter Changes", us: "Free (Regular)", others: "₹2,000 - ₹4,000/year" },
    { feature: "Repair Service", us: "Free", others: "₹500 - ₹2,000 per visit" },
    { feature: "Warranty Issues", us: "Hassle-free replacement", others: "Complex process" }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">Why Choose RentMyRO?</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Experience the best water purification service in Gurgaon with unmatched affordability, reliability, and customer support
          </p>
        </div>
      </section>

      {/* Main Benefits */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => (
              <Card 
                key={item.id} 
                className="group hover:shadow-2xl transition-all duration-300 border-gray-200 hover:border-blue-300 overflow-hidden"
              >
                <CardContent className="p-8 relative">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-full -mr-16 -mt-16 opacity-50" />
                  <div className="relative">
                    <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <div className="text-white">
                        {getIcon(item.icon)}
                      </div>
                    </div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-4">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">RentMyRO vs Buying RO</h2>
            <p className="text-lg text-gray-600">
              See why renting is smarter than buying
            </p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
                    <th className="py-4 px-6 text-left font-semibold">Feature</th>
                    <th className="py-4 px-6 text-center font-semibold">RentMyRO</th>
                    <th className="py-4 px-6 text-center font-semibold">Buying RO</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((row, index) => (
                    <tr 
                      key={index} 
                      className={`border-b border-gray-100 hover:bg-blue-50 transition-colors duration-200 ${
                        index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                      }`}
                    >
                      <td className="py-4 px-6 font-medium text-gray-900">{row.feature}</td>
                      <td className="py-4 px-6 text-center text-green-600 font-semibold">{row.us}</td>
                      <td className="py-4 px-6 text-center text-gray-600">{row.others}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 border-t-2 border-green-200">
              <div className="flex items-center justify-center gap-3">
                <div className="text-2xl font-bold text-green-600">Save up to ₹30,000 in first year!</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Trusted by Thousands</h2>
            <p className="text-lg text-gray-600">What makes us Gurgaon's #1 choice</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&h=600&fit=crop" 
                alt="Happy customers"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
            </div>
            
            <div className="space-y-6">
              {[
                { number: "5000+", label: "Happy Families", desc: "Across Gurgaon trust us for pure water" },
                { number: "50+", label: "Corporate Clients", desc: "Including top MNCs and educational institutions" },
                { number: "5+", label: "Years Experience", desc: "Serving Gurgaon with excellence" },
                { number: "4.9/5", label: "Customer Rating", desc: "Based on verified reviews" }
              ].map((stat, index) => (
                <div key={index} className="flex gap-6 items-start p-6 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl hover:shadow-lg transition-shadow duration-300">
                  <div className="text-4xl font-bold text-blue-600 flex-shrink-0">{stat.number}</div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{stat.label}</h3>
                    <p className="text-sm text-gray-600">{stat.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-cyan-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Experience the RentMyRO Difference
          </h2>
          <p className="text-xl text-blue-50 mb-8">
            Join thousands of satisfied customers. Get started with just ₹499/month
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 shadow-lg transition-all duration-300 transform hover:scale-105">
              Get Your Free Quote
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default WhyChooseUs;
